import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MtnaDialogButton, MtnaDialogComponent, MtnaDialogData, MtnaErrorService } from '@mtna/core-angular';
import { Observable, of, Subject } from 'rxjs';
import { take } from 'rxjs/operators';

import { RdsServerService } from './rds-server.service';

@Injectable({ providedIn: 'root' })
export class RdsEulaService {
  static storageKey = 'rds-c-api-eula-agreement';

  constructor(private dialog: MatDialog, private errorService: MtnaErrorService, private rdsServerService: RdsServerService) {}

  /**
   * @returns Whether the user has signed/agreed to the EULA
   */
  hasUserSigned(): boolean {
    try {
      return !!window.localStorage[RdsEulaService.storageKey];
    } catch {
      return false;
    }
  }

  /**
   * Sign/agree to the EULA
   */
  sign(): void {
    window.localStorage[RdsEulaService.storageKey] = true;
  }

  /**
   * Unsign/disagree to the EULA
   */
  unsign(): void {
    window.localStorage.removeItem(RdsEulaService.storageKey);
  }

  /**
   * Show the disclaimer in a dialog if one exists.
   * @returns Whether the user has agreed to the EULA. If no EULA exists, return true.
   */
  show(): Observable<boolean> {
    const agreed$ = new Subject<boolean>();
    // Get the disclaimer
    this.rdsServerService.getDisclaimer().subscribe(
      (disclaimer) => {
        if (!disclaimer) {
          // If no disclaimer is in effect, return true
          agreed$.next(true);
          agreed$.complete();
        } else {
          // Show disclaimer in a dialog
          const dialogRef = this.dialog.open(MtnaDialogComponent, {
            disableClose: true,
            width: '600px',
            data: new MtnaDialogData('End User License Agreement', disclaimer, [new MtnaDialogButton('AGREE', true, 'raised', 'primary')]),
          });
          dialogRef
            .afterClosed()
            .pipe(take(1))
            .subscribe((agree) => {
              if (agree) {
                this.sign();
              } else {
                this.unsign();
              }
              agreed$.next(this.hasUserSigned());
              agreed$.complete();
            });
        }
      },
      (err) => this.errorService.showError(err)
    );

    // We need to check if agreed$ is closed or stoped because if this.rdsServerService.getDisclaimer()
    // is cached it will return synchonously and will already be completed by the time this method returns
    return agreed$.closed || agreed$.isStopped ? of(true) : agreed$.asObservable();
  }
}
